"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const AppAris_1 = require("./AppAris");
const Index_1 = require("./Index");
const Constraints_1 = require("./Constraints");
const versionPrefix = ';v=';
const uuidRegExpString = '[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}';
const uuidConstraints = [Constraints_1.pattern(new RegExp(`^${uuidRegExpString}$`, 'i'))];
const extensionGroupIdConstraints = [Constraints_1.pattern(new RegExp(`^(${uuidRegExpString}|static)$`, 'i'))];
class AppFamilyAriImpl {
    constructor(json) {
        this.json = json;
        this.validate();
    }
    get appId() {
        return this.json.appId;
    }
    get environmentId() {
        return this.json.environmentId;
    }
    get version() {
        return this.json.version;
    }
    get extensionGroupId() {
        return this.json.extensionGroupId;
    }
    get extensionKey() {
        return this.json.extensionKey;
    }
    toJSON() {
        return this.json;
    }
    static parse(ari) {
        const { resourceOwner, resourceType, resourceId } = ari;
        if (resourceOwner !== AppAris_1.AppFamilyAri.resourceOwner) {
            throw new AppAris_1.NotAppFamilyAriError(ari, `resourceOwner (${resourceOwner}) must be ${AppAris_1.AppFamilyAri.resourceOwner}`);
        }
        if (!resourceType || AppAris_1.AppFamilyAri.appFamilyResourceTypes.indexOf(resourceType) < 0) {
            throw new AppAris_1.NotAppFamilyAriError(ari, `resourceType must be one of ${AppAris_1.AppFamilyAri.appFamilyResourceTypes}`);
        }
        if (!resourceId) {
            throw new AppAris_1.NotAppFamilyAriError(ari, 'resourceId is must be provided');
        }
        const versionIndex = resourceId.indexOf(versionPrefix);
        if (versionIndex > 0 && resourceId.lastIndexOf(versionPrefix) > versionIndex) {
            throw new AppAris_1.NotAppFamilyAriError(ari, 'can only include one version suffix');
        }
        const [mainResourceId, version] = resourceId.split(versionPrefix);
        const segments = mainResourceId.split('/').map(stripToUndefined);
        if (segments.length > 4) {
            throw new AppAris_1.NotAppFamilyAriError(ari, `no path segments can be added after extensionKey ${segments.slice(4)}`);
        }
        const [appId = undefined, environmentId = undefined, providedExtensionGroupId = undefined, extensionKey = undefined] = segments;
        if (!appId) {
            throw new AppAris_1.NotAppFamilyAriError(ari, 'appId must be included');
        }
        const extensionGroupId = providedExtensionGroupId ||
            ((resourceType === AppAris_1.AppFamilyAri.extensionGroupResourceType || resourceType === AppAris_1.AppFamilyAri.extensionResourceType) ? 'static' : undefined);
        if (resourceType === AppAris_1.AppFamilyAri.extensionResourceType && !(environmentId && providedExtensionGroupId && extensionKey)) {
            throw new AppAris_1.NotAppFamilyAriError(ari, `environmentId, extensionGroupId, extensionKey must all be provided for resourceType ${AppAris_1.AppFamilyAri.extensionResourceType}`);
        }
        else if (resourceType === AppAris_1.AppFamilyAri.extensionGroupResourceType && (!(environmentId && extensionGroupId) || extensionKey)) {
            throw new AppAris_1.NotAppFamilyAriError(ari, `environmentId, extensionGroupId must all be provided for resourceType ${AppAris_1.AppFamilyAri.extensionGroupResourceType}`);
        }
        else if (resourceType === AppAris_1.AppFamilyAri.environmentResourceType && (!environmentId || providedExtensionGroupId || extensionKey)) {
            throw new AppAris_1.NotAppFamilyAriError(ari, `environmentId must be provided for resourceType ${AppAris_1.AppFamilyAri.environmentResourceType}`);
        }
        else if (resourceType === AppAris_1.AppFamilyAri.appResourceType && (environmentId || providedExtensionGroupId || extensionKey)) {
            throw new AppAris_1.NotAppFamilyAriError(ari, `environmentId must be provided for resourceType ${AppAris_1.AppFamilyAri.environmentResourceType}`);
        }
        return new AppFamilyAriImpl({ appId, environmentId, extensionGroupId, extensionKey, version });
    }
    asAppAri() {
        return this.returnIf(() => this.isAppAri(), 'app');
    }
    asAppEnvironmentAri() {
        return this.returnIf(() => this.isAppEnvironmentAri(), 'environment');
    }
    asExtensionGroupAri() {
        return this.returnIf(() => this.isExtensionGroupAri(), 'extension-group');
    }
    asExtensionAri() {
        return this.returnIf(() => this.isExtensionAri(), 'extension');
    }
    isAppAri() {
        return !this.isAtLeastAppEnvironmentAri;
    }
    isAppEnvironmentAri() {
        return this.isAtLeastAppEnvironmentAri && !this.isAtLeastExtensionGroupAri;
    }
    isExtensionGroupAri() {
        return this.isAtLeastExtensionGroupAri && !this.isExtensionAri();
    }
    isExtensionAri() {
        return this.isAtLeastExtensionGroupAri && this.extensionKey !== undefined;
    }
    returnIf(check, type) {
        if (!check()) {
            throw new AppAris_1.NotExpectedAppFamilyAriTypeError(type, this);
        }
        return this; // :sadpanda:
    }
    get isAtLeastAppEnvironmentAri() {
        return this.environmentId !== undefined;
    }
    get isAtLeastExtensionGroupAri() {
        return this.isAtLeastAppEnvironmentAri && this.extensionGroupId !== undefined;
    }
    validate() {
        const constraintViolations = [
            Constraints_1.validateField(this, 'environmentId', uuidConstraints),
            Constraints_1.validateField(this, 'appId', uuidConstraints),
            Constraints_1.validateField(this, 'extensionGroupId', extensionGroupIdConstraints),
        ]
            .filter((v) => v);
        if (constraintViolations.length > 0) {
            throw Index_1.InvalidAriError.withConstraintViolations(this.toString(), constraintViolations);
        }
    }
    toAri() {
        const resourceOwner = AppAris_1.AppFamilyAri.resourceOwner;
        const { appId, environmentId, extensionGroupId, extensionKey, version } = this.toJSON();
        function withVersion(segments) {
            const path = segments.join('/');
            if (!version) {
                return path;
            }
            else {
                return `${path}${versionPrefix}${version}`;
            }
        }
        if (this.isExtensionAri()) {
            return new Index_1.ResourceIdentifier({
                resourceOwner, resourceType: AppAris_1.AppFamilyAri.extensionResourceType,
                resourceId: withVersion([appId, environmentId, extensionGroupId, extensionKey])
            });
        }
        else if (this.isExtensionGroupAri()) {
            return new Index_1.ResourceIdentifier({
                resourceOwner, resourceType: AppAris_1.AppFamilyAri.extensionGroupResourceType,
                resourceId: withVersion([appId, environmentId, extensionGroupId])
            });
        }
        else if (this.isAppEnvironmentAri()) {
            return new Index_1.ResourceIdentifier({
                resourceOwner, resourceType: AppAris_1.AppFamilyAri.environmentResourceType,
                resourceId: withVersion([appId, environmentId])
            });
        }
        else {
            return new Index_1.ResourceIdentifier({
                resourceOwner, resourceType: AppAris_1.AppFamilyAri.appResourceType,
                resourceId: appId
            });
        }
    }
    toString() {
        return this.toAri().toString();
    }
}
exports.AppFamilyAriImpl = AppFamilyAriImpl;
function stripToUndefined(str) {
    if (!str) {
        return undefined;
    }
    const trimmed = str.trim();
    if (trimmed.length === 0) {
        return undefined;
    }
    return trimmed;
}
//# sourceMappingURL=AppAriImpls.js.map